import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import pdf from "../../Assets/Images/PDF-icon.svg";
import docx from "../../Assets/Images/DOC-icon.svg";
import xls from "../../Assets/Images/xls_8361442.png";
import txt from "../../Assets/Images/txt.png";
import html from "../../Assets/Images/HTML-icon.svg";
import { Toast } from "../Toast-Snackbar/ToastSnackbar";
import BotResponseStarIcon from "../../Assets/Images/bot-respsonse-star-icon.svg";
import Cookies from "js-cookie";

const sanitizeBotText = (text) => {
  if (!text) return "";
  return text
    .replace(/\(ID:\s*[a-f0-9-]+\)/gi, "")
    .replace(/Candidate ID:\s*[a-f0-9-]+/gi, "")
    .replace(/Job ID:\s*[a-f0-9-]+/gi, "")
    .replace(/File ID:\s*[a-f0-9-]+/gi, "")
    .replace(/\|\s*Candidate ID:\s*[a-f0-9-]+/gi, "")
    .replace(/\(\s*Candidate\s+ID:?\s*[a-f0-9-]+\)/gi, "")
    .replace(/Candidate\s+ID:?\s*[a-f0-9-]+/gi, "")
    .replace(/\|\s*File ID:\s*[a-f0-9-]+/gi, "")
    .replace(/\(\s*\)/g, "")
    .replace(/\[\s*\]/g, "")
    // .replace(/\s{2,}/g, " ")
    .replace(/[ \t]*\n/g, "\n")
    .trim();
};

const parseMessageText = (rawText) => {
  let text = rawText;
  if (Array.isArray(text)) {
    text = text.join("\n");
  }
  if (typeof text !== "string") {
    text = text == null ? "" : String(text);
  }
  text = sanitizeBotText(text);
  const lines = text.split("\n").filter((line) => line.trim() !== "");
  const educationLines = [];
  let isEducationSection = false;
  let currentEducation = null;
  let isSimilarResumesSection = false;
  const summaryLines = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith("Similar Resumes:")) {
      isSimilarResumesSection = true;
      continue;
    }
    if (isSimilarResumesSection && line.match(/\.pdf|\.docx? \(ID:/)) {
      continue;
    }
    if (line.match(/has the following education details:/)) {
      isEducationSection = true;
      continue;
    }
    if (isEducationSection && line.match(/^\d+\.\s*\*\*/)) {
      if (currentEducation) {
        educationLines.push(currentEducation);
      }
      const match = line.match(/^\d+\.\s*\*\*(.*?)\*\*/);
      if (match) {
        currentEducation = { degree: match[1].trim(), details: [] };
      }
    } else if (isEducationSection && line.match(/^\s*-\s*.+/)) {
      const match = line.match(/^\s*-\s*(.+)/);
      if (match && currentEducation) {
        const detail = match[1].trim();
        const [key, value] = detail.split(":").map((item) => item.trim());
        if (key && value) {
          currentEducation.details.push({ key, value });
        }
      }
    } else if (!isSimilarResumesSection) {
      summaryLines.push(line);
    }
  }
  if (currentEducation) {
    educationLines.push(currentEducation);
  }
  let summary = summaryLines
  .filter((line) => {
    const trimmed = line.trim();
    if (/^-\s*Candidate ID:/i.test(trimmed)) return false;
    return true;
  })
  .join("\n");
  return {
    summary,
    skillsLines: summaryLines.filter((line) =>
      line.match(/^(Email|Phone|Years of Experience):/)
    ),
    educationLines,
  };
};

// Renders inline markdown (**bold**, *italic*/_italic_, `code`) within a single
// line of bot text. Returns an array of React nodes. Pure string/regex work, so
// it adds no meaningful render cost. Block-level structure (bullets, paragraphs)
// is handled by the caller; this only styles inline spans.
const renderInlineMarkdown = (text, keyPrefix = "md") => {
  if (text == null) return null;
  const str = String(text);
  const pattern = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*\n]+\*|_[^_\n]+_|`[^`]+`)/g;
  const segments = str.split(pattern);
  return segments.map((segment, idx) => {
    if (!segment) return null;
    const key = `${keyPrefix}-${idx}`;
    if (
      (segment.startsWith("**") && segment.endsWith("**")) ||
      (segment.startsWith("__") && segment.endsWith("__"))
    ) {
      return <strong key={key}>{segment.slice(2, -2)}</strong>;
    }
    if (
      (segment.startsWith("*") && segment.endsWith("*")) ||
      (segment.startsWith("_") && segment.endsWith("_"))
    ) {
      return <em key={key}>{segment.slice(1, -1)}</em>;
    }
    if (segment.startsWith("`") && segment.endsWith("`")) {
      return <code key={key}>{segment.slice(1, -1)}</code>;
    }
    // Plain text between matched spans: drop orphan markdown asterisks left by
    // asymmetric/odd LLM markdown (e.g. a stray "*" with no pair).
    return segment.replace(/\*/g, "");
  });
};

// Returns the content of a markdown bullet line ("-", "*", or "•" followed by
// whitespace), or null if the line is not a bullet. "**bold**" is intentionally
// not treated as a bullet because the first "*" is not followed by whitespace.
const getBulletContent = (trimmedLine) => {
  if (typeof trimmedLine !== "string") return null;
  const match = trimmedLine.match(/^[-*•]\s+/);
  return match ? trimmedLine.slice(match[0].length) : null;
};

// Renders a single (non-bullet) line of bot text. Markdown headings (#, ##, ...)
// render as a bold, slightly larger span with the # markers stripped; numbered
// list lines and ordinary paragraphs flow through inline markdown unchanged.
// Cost is one regex test per line, so it adds no meaningful render time. Bullets
// are handled separately by the caller via getBulletContent.
const renderBotContent = (line, keyPrefix = "bl") => {
  if (typeof line !== "string") return renderInlineMarkdown(line, keyPrefix);
  const headingMatch = line.match(/^\s*#{1,6}\s+(.*)$/);
  if (headingMatch) {
    return (
      <span style={{ fontWeight: 700, fontSize: "1.05em" }}>
        {renderInlineMarkdown(headingMatch[1], keyPrefix)}
      </span>
    );
  }
  return renderInlineMarkdown(line, keyPrefix);
};

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    const scrollableElements = document.querySelectorAll("*");
    scrollableElements.forEach((element) => {
      if (element.scrollHeight > element.clientHeight) {
        element.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    });
  }, [pathname]);
  return null;
};
const formatDate = (date, month, showTime = false) => {
  const options = { month: "short", day: "2-digit", year: "numeric" };
  const monthoptions = { month: "short", day: "2-digit" };
  if (showTime) {
    const d = new Date(date);
    return `${d.toLocaleDateString("en-US", options)} ( ${d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })} )`;
  } else if (month) {
    return new Date(date).toLocaleDateString("en-US", monthoptions);
  } else {
    return new Date(date).toLocaleDateString("en-US", options);
  }
};

const formatDateTime = (date) => {
  if (!date) return "N/A";
  const d = new Date(date);
  const options = {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  return d.toLocaleString("en-US", options);
};

const formatNotificationBody = (body) => {
  if (!body) return body;
  const isoDateRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z?/g;

  return body.replace(isoDateRegex, (match) => {
    try {
      return formatDateTime(match);
    } catch (error) {
      console.error("Error formatting date in notification:", error);
      return match;
    }
  });
};

const getTimeDifference = (lastIndexed) => {
  const currentTime = new Date();
  const lastIndexTime = new Date(lastIndexed);
  const timeDifference = currentTime - lastIndexTime;

  const minuteDifference = Math.floor(timeDifference / (1000 * 60));
  const hourDifference = Math.floor(timeDifference / (1000 * 60 * 60));

  if (hourDifference >= 1) {
    return `${hourDifference} hour(s) ago`;
  } else if (minuteDifference >= 1) {
    return `${minuteDifference} minute(s) ago`;
  } else {
    return "Just now";
  }
};

const countDocumentsInFolder = (folderName, AllUploadedFiles) => {
  const filesInFolder = AllUploadedFiles.filter(
    (file) => file.folder === folderName
  );
  return filesInFolder.length;
};

const countTotalFilesInFolder = (folder) => {
  let totalFiles = folder.files.length;

  for (const subFolder of folder.subFolders) {
    totalFiles += countTotalFilesInFolder(subFolder);
  }

  return totalFiles;
};
const displayFileName = (fileName) => {
  const maxLength = 25;
  if (fileName.length >= maxLength) {
    const extensionIndex = fileName.lastIndexOf(".");
    const extension = fileName.substring(extensionIndex);
    const start = fileName.substring(0, 10);
    const end = fileName.substring(extensionIndex - 5, extensionIndex);
    return `${start}...${end}${extension}`;
  }
  return fileName;
};
const handleClear = (formik) => {
  formik.resetForm();
};
function CustomDragAndDrop(onDrop, multiFile = false) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (multiFile) {
      onDrop(files);
    } else {
      const firstFile = files.length > 0 ? files[0] : null;
      if (firstFile) {
        onDrop([firstFile]);
      }
    }
  };
  return {
    isDragging,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
}
const handleIcon = (fileName) => {
  const extension = fileName?.split(".").pop().toLowerCase();
  switch (extension) {
    case "pdf":
      return pdf;
    case "docx":
    case "doc":
      return docx;
    case "html":
    case "htm":
      return html;
    case "xls":
    case "xlsx":
      return xls;
    case "txt":
      return txt;
    default:
      return null;
  }
};
const handleFileSelect = (droppedFiles, allowedFormats, maxSizeMB) => {
  const validFiles = [];
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  for (let file of droppedFiles) {
    const fileExtension = file.name
      .slice(file.name.lastIndexOf("."))
      .toLowerCase();
    const isValidFormat = allowedFormats.includes(fileExtension);
    const isValidSize = file.size <= maxSizeBytes;
    if (!isValidFormat) {
      Toast(
        "warning",
        `Unsupported file format. Please upload files in ${allowedFormats.join(
          ", "
        )} formats.`
      );
    } else if (!isValidSize) {
      Toast("warning", `File size exceeds the limit of ${maxSizeMB}MB.`);
    } else {
      validFiles.push(file);
    }
  }

  return validFiles;
};
function handleCustomTrim(values) {
  const trimmedValues = { ...values };
  for (let key in trimmedValues) {
    if (typeof trimmedValues[key] === "string") {
      trimmedValues[key] = trimmedValues[key].trim();
    } else if (Array.isArray(trimmedValues[key])) {
      trimmedValues[key] = trimmedValues[key].map((item) =>
        typeof item === "string" ? item.trim() : item
      );
    }
  }
  return trimmedValues;
}

const handleTriggerClear = (formik, setClearTriggered) => {
  formik.resetForm();
  setClearTriggered(true);
};
const Popupscroll = (isDialogOpen = false) => {
  if (isDialogOpen) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "auto";
  }
};
const groupByDateCategory = (items) => {
  const today = new Date();
  const todayUTC = new Date(
    Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  );

  return items.reduce((groups, item) => {
    if (!item.UpdatedAt) return groups;

    const updatedAt = new Date(item.UpdatedAt);
    const updatedAtUTC = new Date(
      Date.UTC(
        updatedAt.getFullYear(),
        updatedAt.getMonth(),
        updatedAt.getDate()
      )
    );

    const diffInDays = Math.floor(
      (todayUTC - updatedAtUTC) / (1000 * 60 * 60 * 24)
    );

    let category = "Older";
    if (diffInDays === 0) category = "Today";
    else if (diffInDays === 1) category = "Yesterday";
    else if (diffInDays <= 7) category = "Previous 7 Days";
    else if (diffInDays <= 30) category = "Previous 30 Days";

    if (!groups[category]) groups[category] = [];
    groups[category].push(item);
    return groups;
  }, {});
};

const candidateCroupByDateCategory = (items) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const groups = {};

  items.forEach((item) => {
    const dateStr =
      item.application_date ||
      item.tracked_date ||
      item.created_at ||
      item.CreatedAt ||
      item.date ||
      item.timestamp ||
      null;

    if (!dateStr) return;

    const trackedDate = new Date(dateStr);
    if (isNaN(trackedDate.getTime())) return;

    trackedDate.setHours(0, 0, 0, 0);

    const diffInDays = Math.floor(
      (today - trackedDate) / (1000 * 60 * 60 * 24)
    );

    let category = "Older";
    if (diffInDays === 0) category = "Today";
    else if (diffInDays === 1) category = "Yesterday";
    else if (diffInDays <= 7) category = "Previous 7 Days";
    else if (diffInDays <= 30) category = "Previous 30 Days";

    if (!groups[category]) groups[category] = [];
    groups[category].push(item);
  });

  return groups;
};

const candidateGroupByLatestTrackDate = (items) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const groups = {};

  items.forEach((item) => {
    if (!item.applications || item.applications.length === 0) return;
    const latestApp = item.applications[0];
    const dateStr = latestApp.updated_at || latestApp.UpdatedAt || null;
    if (!dateStr) return;

    const trackDate = new Date(dateStr);
    if (isNaN(trackDate.getTime())) return;

    trackDate.setHours(0, 0, 0, 0);

    const diffInDays = Math.floor((today - trackDate) / (1000 * 60 * 60 * 24));

    let category = "Older";
    if (diffInDays === 0) category = "Today";
    else if (diffInDays === 1) category = "Yesterday";
    else if (diffInDays <= 7) category = "Previous 7 Days";
    else if (diffInDays <= 30) category = "Previous 30 Days";

    if (!groups[category]) groups[category] = [];
    groups[category].push(item);
  });

  return groups;
};

const LOGOUT_BUTTON_USAGE_KEY = "resume_parser_button_usage";

const handleLogoutCommon = (options = {}) => {
  const {
    setPendingNavigationPath,
    setMobileMenuOpen,
    setPopupVisible,
    setUserDetails,
    setShowResumeParser,
    setIsParsingComplete,
    setUploadedResumes,
    setIncompleteFiles,
    setParsedData,
    setIncompleteData,
    setRunSetId,
    setErrorMessages,
    setSuccessMessages,
    setShowFailedTab,
    afterLogoutNavigate,
    preserveSessionFlags = false,
    disableDefaultNavigate = false,
  } = options;

  localStorage.removeItem(LOGOUT_BUTTON_USAGE_KEY);
  localStorage.removeItem("token");
  localStorage.removeItem("closeTab");
  localStorage.removeItem("tokenExpiry");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("monstertokenInd");
  localStorage.removeItem("monsterTokenUsa");
  localStorage.removeItem("monsterTokenUsaExpiry");
  localStorage.removeItem("monsterCandidateIdMap");
  localStorage.removeItem("diceCandidateIdMap");
  localStorage.removeItem("user");
  localStorage.removeItem("userTimezone");

  localStorage.removeItem("sessionId");
  if (!preserveSessionFlags) {
    localStorage.removeItem("sessionExpired");
    localStorage.removeItem("sessionExpiredReason");
  }
  localStorage.removeItem("retryApiResponses");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("access_token_expires_at");
  localStorage.removeItem("tenant")

  Cookies.remove("refreshToken");
  Cookies.remove("refreshTokenExpiry");

  if (setPendingNavigationPath) setPendingNavigationPath("/");
  if (setMobileMenuOpen) setMobileMenuOpen(false);
  if (setShowFailedTab) setShowFailedTab(false);
  if (setUserDetails) setUserDetails(null);
  if (setPopupVisible) setPopupVisible(false);
  if (setShowResumeParser) setShowResumeParser(false);
  if (setIsParsingComplete) setIsParsingComplete(false);
  if (setUploadedResumes) setUploadedResumes([]);
  if (setIncompleteFiles) setIncompleteFiles([]);
  if (setParsedData) setParsedData([]);
  if (setIncompleteData) setIncompleteData([]);
  if (setRunSetId) setRunSetId(null);
  if (setErrorMessages) setErrorMessages([]);
  if (setSuccessMessages) setSuccessMessages([]);

  if (afterLogoutNavigate) {
    afterLogoutNavigate();
  } else if (!disableDefaultNavigate) {
    window.location.href = "/login";
  }
};

const createCandidateGroupByDateCategory = (items) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const groups = {};

  items.forEach((item) => {
    const dateStr =
      item.application_date ||
      item.created_date ||
      item.created_at ||
      item.CreatedAt ||
      item.date ||
      item.timestamp ||
      null;
    if (!dateStr) return;

    const createdDate = new Date(dateStr);
    if (isNaN(createdDate.getTime())) return;

    createdDate.setHours(0, 0, 0, 0);
    const diffInDays = Math.floor(
      (today - createdDate) / (1000 * 60 * 60 * 24)
    );

    let category = "Older";
    if (diffInDays === 0) category = "Today";
    else if (diffInDays === 1) category = "Yesterday";

    if (!groups[category]) groups[category] = [];
    groups[category].push(item);
  });

  Object.keys(groups).forEach((cat) => {
    groups[cat].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  });

  return groups;
};

function applyEllipsis(value, maxLength = 20, suffix = "...") {
  if (typeof value !== "string") return value;
  return value.length > maxLength
    ? value.substring(0, maxLength) + suffix
    : value;
}

const convertToBulletPoints = (text) => {
  if (!text) return [];
  const lines = text.split("\n");
  const points = lines.flatMap((line) => {
    const trimmed = line.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("•")) {
      return [trimmed.substring(1).trim()];
    }
    return trimmed
      .split(/\.\s+/)
      .filter((s) => s.length > 0)
      .map((s) => s.trim());
  });

  return points.filter((point) => point.length > 0);
};
const getStageStyles = (stage) => {
  switch (stage?.toLowerCase()) {
    case "new profile":
      return {
        backgroundColor: "var(--chip-bg-new-profile)",
        color: "var(--chip-text-new-profile)",
        border: "1px solid var(--chip-border-new-profile)",
        marginTop: "-2px",
      };
    case "not spoken - vm / nr - emailed":
      return {
        backgroundColor: "var(--chip-bg-not-spoken)",
        color: "var(--chip-text-not-spoken)",
        border: "1px solid var(--chip-border-not-spoken)",
        marginTop: "-2px",
      };
    case "spoken - to track":
      return {
        backgroundColor: "var(--chip-bg-spoken-to-track)",
        color: "var(--chip-text-spoken-to-track)",
        border: "1px solid var(--chip-border-spoken-to-track)",
        marginTop: "-2px",
      };
    case "not suitable":
      return {
        backgroundColor: "var(--chip-bg-not-suitable)",
        color: "var(--chip-text-not-suitable)",
        border: "1px solid var(--chip-border-not-suitable)",
        marginTop: "-2px",
      };
    case "not interested":
      return {
        backgroundColor: "var(--chip-bg-not-interested)",
        color: "var(--chip-text-not-interested)",
        border: "1px solid var(--chip-border-not-interested)",
        marginTop: "-2px",
      };
    case "interested":
      return {
        backgroundColor: "var(--chip-bg-interested)",
        color: "var(--chip-text-interested)",
        border: "1px solid var(--chip-border-interested)",
        marginTop: "-2px",
      };
    case "proposed":
      return {
        backgroundColor: "var(--chip-bg-proposed)",
        color: "var(--chip-text-proposed)",
        border: "1px solid var(--chip-border-proposed)",
        marginTop: "-2px",
      };
    case "shortlisted":
      return {
        backgroundColor: "var(--chip-bg-shortlisted)",
        color: "var(--chip-text-shortlisted)",
        border: "1px solid var(--chip-border-shortlisted)",
        marginTop: "-2px",
      };
    case "interview scheduled":
      return {
        backgroundColor: "var(--chip-bg-interview-scheduled)",
        color: "var(--chip-text-interview-scheduled)",
        border: "1px solid var(--chip-border-interview-scheduled)",
        marginTop: "-2px",
      };
    case "interview reject":
      return {
        backgroundColor: "var(--chip-bg-interview-reject)",
        color: "var(--chip-text-interview-reject)",
        border: "1px solid var(--chip-border-interview-reject)",
        marginTop: "-2px",
      };
    case "offered":
      return {
        backgroundColor: "var(--chip-bg-offered)",
        color: "var(--chip-text-offered)",
        border: "1px solid var(--chip-border-offered)",
        marginTop: "-2px",
      };
    case "offer rejected - client":
      return {
        backgroundColor: "var(--chip-bg-offer-rejected-client)",
        color: "var(--chip-text-offer-rejected-client)",
        border: "1px solid var(--chip-border-offer-rejected-client)",
        marginTop: "-2px",
      };
    case "offer declined - candidate":
      return {
        backgroundColor: "var(--chip-bg-offer-declined-candidate)",
        color: "var(--chip-text-offer-declined-candidate)",
        border: "1px solid var(--chip-border-offer-declined-candidate)",
        marginTop: "-2px",
      };
    case "joined":
      return {
        backgroundColor: "var(--chip-bg-joined)",
        color: "var(--chip-text-joined)",
        border: "1px solid var(--chip-border-joined)",
        marginTop: "-2px",
      };
    case "joined - client reject":
      return {
        backgroundColor: "var(--chip-bg-joined-client-reject)",
        color: "var(--chip-text-joined-client-reject)",
        border: "1px solid var(--chip-border-joined-client-reject)",
        marginTop: "-2px",
      };
    case "project end":
      return {
        backgroundColor: "var(--chip-bg-project-end)",
        color: "var(--chip-text-project-end)",
        border: "1px solid var(--chip-border-project-end)",
        marginTop: "-2px",
      };
    case "fake":
      return {
        backgroundColor: "var(--chip-bg-fake)",
        color: "var(--chip-text-fake)",
        border: "1px solid var(--chip-border-fake)",
        marginTop: "-2px",
      };
    case "withdrawn":
      return {
        backgroundColor: "var(--chip-bg-withdrawn)",
        color: "var(--chip-text-withdrawn)",
        border: "1px solid var(--chip-border-withdrawn)",
        marginTop: "-2px",
      };
    default:
      return {
        backgroundColor: "var(--chip-bg-default)",
        color: "var(--chip-text-default)",
        border: "1px solid var(--chip-border-default)",
        marginTop: "-2px",
      };
  }
};
function toCapitalize(str) {
  if (!str) return "";
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}
function toCamelCase(value) {
  if (typeof value !== "string") return value;
  const words = value.trim().match(/[a-zA-Z0-9]+/g);
  if (!words) return "";

  return words
    .map((word, index) => {
      const normalizedWord = word.toLowerCase();
      if (index === 0) return normalizedWord;
      return normalizedWord.charAt(0).toUpperCase() + normalizedWord.slice(1);
    })
    .join("");
}
const formatInterviewerName = (name) => {
  if (!name) return "";
  return name.replace(/-\d+$/, "").replace(/-/g, " ").trim();
};
const roundUpExperience = (exp) => {
  if (exp === 0 || exp === "0") {
    return "<1 year";
  }

  const numExp = Number(exp);
  if (!exp || isNaN(numExp) || numExp < 0) {
    return "N/A";
  }
  const formattedExp = parseFloat(numExp.toFixed(1));
  return `${formattedExp} ${formattedExp === 1 ? "year" : "years"}`;
};

const filterPayload = (obj) => {
  const newObj = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      if (value !== "" && value !== null && value !== undefined) {
        newObj[key] = value;
      }
    }
  }
  return newObj;
};
const toSnakeCase = (str) => str.toLowerCase().replace(/\s+/g, "_");

const getRecruiterName = (log) =>
  [log?.first_name, log?.last_name].filter(Boolean).join(" ") || "Unknown";

const formatDateTimeWithTimeZone = (dateString, timeZone) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  const dateOptions = {
    timeZone,
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  const timeOptions = {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  return `${date.toLocaleDateString("en-US", dateOptions)} ( ${date.toLocaleTimeString("en-US", timeOptions)} )`;
};

export {
  getTimeDifference,
  countDocumentsInFolder,
  countTotalFilesInFolder,
  displayFileName,
  formatDate,
  CustomDragAndDrop,
  handleClear,
  handleIcon,
  handleFileSelect,
  handleCustomTrim,
  handleTriggerClear,
  Popupscroll,
  ScrollToTop,
  groupByDateCategory,
  candidateCroupByDateCategory,
  candidateGroupByLatestTrackDate,
  createCandidateGroupByDateCategory,
  applyEllipsis,
  convertToBulletPoints,
  getStageStyles,
  toCapitalize,
  toCamelCase,
  formatInterviewerName,
  BotResponseStarIcon,
  sanitizeBotText,
  parseMessageText,
  renderInlineMarkdown,
  getBulletContent,
  renderBotContent,
  handleLogoutCommon,
  roundUpExperience,
  filterPayload,
  formatDateTime,
  formatNotificationBody,
  toSnakeCase,
  getRecruiterName,
  formatDateTimeWithTimeZone,
};
